import { useCallback, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Controller, useForm } from 'react-hook-form';
import { EmailInput, ErrorText } from '@buoyhealth/common.buoy-input';
import TermsAndConditions from 'src/interviews/components/EmailTranscript/TermsAndConditions';
import { Paragraph } from 'src/interviews/components/Layout/Embedded';
import SubmitButton from 'src/interviews/components/SubmitButton';
import { trackFormSubmitted } from 'src/interviews/components/EmailTranscript/analytics';
import useTransition from 'src/interviews/hooks/useTransition';
import useInterview from 'src/interviews/hooks/useInterview';
import useIntermediary from '../../hooks/useIntermediary';
import type { Form } from './types';
import useSubmitForm from './hooks/useSubmitForm';

type MoreInfoProps = {
  isEmbedded?: boolean;
};
const MoreInfo = ({ isEmbedded }: MoreInfoProps) => {
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [termsModified, setTermsModified] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const shouldShowTermsError = !termsAgreed && termsModified;

  const { message, variant } = useIntermediary() || {};
  const transition = useTransition();
  const { control, getValues, handleSubmit } = useForm<Form>({
    defaultValues: {
      email: '',
      termsAgreed: false,
      newsletterAgreed: false,
    },
  });
  const { token } = useInterview();
  const formValues = getValues();
  const { email } = formValues;

  const mutation = useSubmitForm(email);

  const termsBackgroundColor = isEmbedded ? '#FFFFFF' : '#ECF2FE';
  const onSubmit = useCallback(
    async (data: Form) => {
      try {
        if (data.newsletterAgreed && data.termsAgreed) {
          await mutation.mutateAsync();
          setSubmitError(false);
          trackFormSubmitted(token || '', data.newsletterAgreed, 'polaris');
          transition({
            event: 'INTERMEDIARY_SUBMIT',
            data: {
              message,
              variant,
            },
          });
        }
      } catch {
        setSubmitError(true);
      }
    },
    [message, mutation, token, transition, variant],
  );

  return (
    <Grid item>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Paragraph>{message}</Paragraph>
        </Grid>

        <Grid item className="!bp-pt-5">
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => {
              const { ref: inputRef, ...fieldProps } = field;
              return (
                <EmailInput
                  className="bp-h-[54px] bp-max-w-[328px]"
                  error={!!fieldState.error}
                  fieldProps={fieldProps}
                  inputRef={inputRef}
                  onChange={(event) => {
                    field.onChange(event);
                  }}
                />
              );
            }}
            rules={{ required: true, pattern: /\S+@\S+\.\S+/ }}
          />
        </Grid>
        <Grid item>
          <Controller
            name="termsAgreed"
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <TermsAndConditions
                  customStyle={{ backgroundColor: termsBackgroundColor, padding: '12px' }}
                  termsCopy={'I agree to'}
                  termsAgreed={field.value}
                  onChange={(event) => {
                    field.onChange(event);
                    setTermsAgreed(event.target.checked);
                    setTermsModified(fieldState.isDirty);
                  }}
                />
              </div>
            )}
          />
          <ErrorText show={shouldShowTermsError} message={'Required'} />
          <Controller
            name="newsletterAgreed"
            control={control}
            render={({ field }) => (
              <div>
                <TermsAndConditions
                  customStyle={{ backgroundColor: termsBackgroundColor, padding: '12px' }}
                  consentCopy={
                    'I would also like to receive relevant healthcare news and offer emails from Buoy Health.'
                  }
                  termsAgreed={field.value}
                  onChange={(event) => {
                    field.onChange(event);
                  }}
                />
              </div>
            )}
          />
          <ErrorText
            show={submitError}
            message={'An error occurred while submitting. Please try again.'}
          />
        </Grid>
      </Grid>
      <SubmitButton onClick={handleSubmit(onSubmit)} isIntermediary={true} text="Submit" />
    </Grid>
  );
};

export default MoreInfo;
